import React, { useState, useEffect, useCallback } from "react";
import {
  MapPin, Search, Play, RefreshCw, TrendingUp, TrendingDown,
  Eye, ChevronDown, Settings, Plus, Trash2, X,
} from "lucide-react";
import {
  MapContainer, TileLayer, CircleMarker, Tooltip, useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as ReTooltip, ResponsiveContainer,
} from "recharts";
import api from "../api/client";

/* ── colour helpers ─────────────────────────────────────────── */
function posColor(pos) {
  if (pos === null || pos === undefined) return "#6b7280"; // gray
  if (pos <= 3) return "#22c55e";   // green
  if (pos <= 10) return "#84cc16";  // lime
  if (pos <= 20) return "#eab308";  // yellow
  return "#ef4444";                 // red
}

function posLabel(pos) {
  if (pos === null || pos === undefined) return "N/R";
  return `#${pos}`;
}

/* ── Map auto-fit ───────────────────────────────────────────── */
function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    try {
      const bounds = points
        .filter((p) => p.latitude && p.longitude)
        .map((p) => [parseFloat(p.latitude), parseFloat(p.longitude)]);
      if (bounds.length > 0) {
        map.fitBounds(bounds, { padding: [30, 30] });
      }
    } catch (e) {
      // Leaflet can throw during zoom transitions — safe to ignore
    }
  }, [points, map]);
  return null;
}

/* ── Grid Point Manager Modal ───────────────────────────────── */
function GridPointModal({ siteId, onClose }) {
  const [points, setPoints] = useState([]);
  const [form, setForm] = useState({ label: "", latitude: "", longitude: "", zip_code: "", neighborhood: "" });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/websites/${siteId}/grid-points/`).then((r) => {
      setPoints(r.data.results || r.data);
      setLoading(false);
    });
  }, [siteId]);

  useEffect(load, [load]);

  const add = () => {
    api.post(`/websites/${siteId}/grid-points/`, { ...form, website: siteId }).then(() => {
      setForm({ label: "", latitude: "", longitude: "", zip_code: "", neighborhood: "" });
      load();
    });
  };

  const remove = (id) => {
    api.delete(`/websites/${siteId}/grid-points/${id}/`).then(load);
  };

  const toggle = (pt) => {
    api.patch(`/websites/${siteId}/grid-points/${pt.id}/`, { is_active: !pt.is_active }).then(load);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-dark-300 rounded-xl border border-dark-50 w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-dark-50">
          <h3 className="text-lg font-semibold text-white">Manage Grid Points</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
        </div>

        {/* Add form */}
        <div className="p-4 border-b border-dark-50 grid grid-cols-6 gap-2">
          {["label", "latitude", "longitude", "zip_code", "neighborhood"].map((f) => (
            <input
              key={f}
              placeholder={f.replace("_", " ")}
              value={form[f]}
              onChange={(e) => setForm({ ...form, [f]: e.target.value })}
              className="bg-dark-200 border border-dark-50 rounded px-2 py-1.5 text-sm text-white placeholder-gray-600"
            />
          ))}
          <button onClick={add} className="bg-copper hover:bg-copper/80 text-white rounded px-3 py-1.5 text-sm flex items-center gap-1">
            <Plus size={14} /> Add
          </button>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-1">
          {loading ? (
            <p className="text-gray-500 text-sm">Loading...</p>
          ) : (
            points.map((pt) => (
              <div key={pt.id} className="flex items-center justify-between bg-dark-200 rounded px-3 py-2 text-sm">
                <div className="flex items-center gap-3">
                  <button onClick={() => toggle(pt)} className={`w-2 h-2 rounded-full ${pt.is_active ? "bg-green-500" : "bg-gray-600"}`} />
                  <span className="text-white font-medium">{pt.label}</span>
                  <span className="text-gray-500">{pt.latitude}, {pt.longitude}</span>
                  {pt.neighborhood && <span className="text-gray-600">({pt.neighborhood})</span>}
                </div>
                <button onClick={() => remove(pt.id)} className="text-gray-600 hover:text-red-400"><Trash2 size={14} /></button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

/* ── SERP Config Modal ─────────────────────────────────────── */
function SerpConfigModal({ onClose }) {
  const [configs, setConfigs] = useState([]);
  const [form, setForm] = useState({ provider: "valueserp", api_key: "", monthly_quota: 2500 });

  const load = () => api.get("/serp-config/").then((r) => setConfigs(r.data.results || r.data));
  useEffect(load, []);

  const save = () => {
    api.post("/serp-config/", form).then(() => {
      setForm({ provider: "valueserp", api_key: "", monthly_quota: 2500 });
      load();
    });
  };

  const remove = (id) => api.delete(`/serp-config/${id}/`).then(load);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-dark-300 rounded-xl border border-dark-50 w-full max-w-lg">
        <div className="flex items-center justify-between p-4 border-b border-dark-50">
          <h3 className="text-lg font-semibold text-white">SERP API Configuration</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
        </div>

        <div className="p-4 space-y-3">
          <select
            value={form.provider}
            onChange={(e) => setForm({ ...form, provider: e.target.value })}
            className="w-full bg-dark-200 border border-dark-50 rounded px-3 py-2 text-sm text-white"
          >
            <option value="valueserp">ValueSERPs</option>
            <option value="serpapi">SerpAPI</option>
            <option value="dataforseo">DataForSEO</option>
            <option value="serper">Serper.dev</option>
          </select>
          <input
            placeholder="API Key"
            value={form.api_key}
            onChange={(e) => setForm({ ...form, api_key: e.target.value })}
            className="w-full bg-dark-200 border border-dark-50 rounded px-3 py-2 text-sm text-white placeholder-gray-600"
          />
          <input
            type="number"
            placeholder="Monthly Quota"
            value={form.monthly_quota}
            onChange={(e) => setForm({ ...form, monthly_quota: parseInt(e.target.value) || 0 })}
            className="w-full bg-dark-200 border border-dark-50 rounded px-3 py-2 text-sm text-white placeholder-gray-600"
          />
          <button onClick={save} className="w-full bg-copper hover:bg-copper/80 text-white rounded py-2 text-sm font-medium">
            Add Provider
          </button>
        </div>

        <div className="p-4 border-t border-dark-50 space-y-2">
          {configs.map((c) => (
            <div key={c.id} className="flex items-center justify-between bg-dark-200 rounded px-3 py-2 text-sm">
              <div>
                <span className="text-white font-medium">{c.provider_display}</span>
                <span className="text-gray-500 ml-2">{c.api_key_masked}</span>
                <span className="text-gray-600 ml-2">{c.queries_used_this_month}/{c.monthly_quota}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${c.is_active ? "bg-green-500" : "bg-gray-600"}`} />
                <button onClick={() => remove(c.id)} className="text-gray-600 hover:text-red-400"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          {configs.length === 0 && <p className="text-gray-600 text-sm">No providers configured yet.</p>}
        </div>
      </div>
    </div>
  );
}

/* ── Main Component ─────────────────────────────────────────── */
export default function LocalRankings({ selectedSite }) {
  const siteId = selectedSite?.id;
  const [keywords, setKeywords] = useState([]);
  const [selectedKw, setSelectedKw] = useState(null);
  const [heatmapData, setHeatmapData] = useState({ scan: null, points: [] });
  const [scans, setScans] = useState([]);
  const [trends, setTrends] = useState([]);
  const [overview, setOverview] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [tab, setTab] = useState("heatmap"); // heatmap | table | trends
  const [showGridModal, setShowGridModal] = useState(false);
  const [showSerpModal, setShowSerpModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // Load keywords
  useEffect(() => {
    if (!siteId) return;
    api.get(`/websites/${siteId}/keywords/`).then((r) => {
      const kws = r.data.results || r.data;
      setKeywords(kws);
      if (kws.length > 0 && !selectedKw) setSelectedKw(kws[0]);
    });
  }, [siteId]);

  // Load overview
  useEffect(() => {
    if (!siteId) return;
    api.get(`/websites/${siteId}/local-rankings/overview/`).then((r) => setOverview(r.data)).catch(() => {});
  }, [siteId]);

  // Load heatmap + scans + trends when keyword changes
  const loadData = useCallback(() => {
    if (!siteId || !selectedKw) return;
    setLoading(true);

    const p1 = api.get(`/websites/${siteId}/local-rankings/heatmap/`, { params: { keyword_id: selectedKw.id } })
      .then((r) => setHeatmapData(r.data))
      .catch(() => setHeatmapData({ scan: null, points: [] }));

    const p2 = api.get(`/websites/${siteId}/local-rankings/scans/`, { params: { keyword_id: selectedKw.id } })
      .then((r) => setScans(r.data))
      .catch(() => setScans([]));

    const p3 = api.get(`/websites/${siteId}/local-rankings/trends/`, { params: { keyword_id: selectedKw.id } })
      .then((r) => setTrends(r.data))
      .catch(() => setTrends([]));

    Promise.all([p1, p2, p3]).finally(() => setLoading(false));
  }, [siteId, selectedKw]);

  useEffect(loadData, [loadData]);

  const triggerScan = () => {
    if (!siteId || !selectedKw) return;
    setScanning(true);
    api.post(`/websites/${siteId}/local-rankings/scan/`, { keyword_id: selectedKw.id })
      .then(() => {
        setTimeout(loadData, 3000);
      })
      .finally(() => setScanning(false));
  };

  const scan = heatmapData.scan;
  const points = heatmapData.points || [];

  // Overview stats
  const kwOverview = overview.find((o) => o.keyword_id === selectedKw?.id);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <MapPin className="text-copper" size={24} /> Local Rank Tracker
          </h1>
          <p className="text-sm text-gray-500 mt-1">Geo-grid ranking heatmap across service areas</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowGridModal(true)} className="px-3 py-2 bg-dark-200 border border-dark-50 rounded-lg text-sm text-gray-300 hover:text-white flex items-center gap-1.5">
            <MapPin size={14} /> Grid Points
          </button>
          <button onClick={() => setShowSerpModal(true)} className="px-3 py-2 bg-dark-200 border border-dark-50 rounded-lg text-sm text-gray-300 hover:text-white flex items-center gap-1.5">
            <Settings size={14} /> API Config
          </button>
          <button
            onClick={triggerScan}
            disabled={scanning || !selectedKw}
            className="px-4 py-2 bg-copper hover:bg-copper/80 disabled:opacity-50 text-white rounded-lg text-sm font-medium flex items-center gap-1.5"
          >
            {scanning ? <RefreshCw size={14} className="animate-spin" /> : <Play size={14} />}
            Scan Now
          </button>
        </div>
      </div>

      {/* Keyword selector + stats bar */}
      <div className="bg-dark-300 rounded-xl border border-dark-50 p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative">
            <select
              value={selectedKw?.id || ""}
              onChange={(e) => {
                const kw = keywords.find((k) => k.id === parseInt(e.target.value));
                setSelectedKw(kw);
              }}
              className="bg-dark-200 border border-dark-50 rounded-lg px-4 py-2 pr-8 text-sm text-white appearance-none min-w-[220px]"
            >
              {keywords.map((kw) => (
                <option key={kw.id} value={kw.id}>{kw.keyword}</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
          </div>

          {scan && (
            <>
              <div className="flex items-center gap-6 text-sm">
                <div>
                  <span className="text-gray-500">Visibility</span>
                  <span className="ml-2 text-white font-bold text-lg">{scan.visibility_score}%</span>
                  {kwOverview?.prev_visibility != null && (
                    <span className={`ml-1 text-xs ${scan.visibility_score >= kwOverview.prev_visibility ? "text-green-400" : "text-red-400"}`}>
                      {scan.visibility_score >= kwOverview.prev_visibility ? "+" : ""}{scan.visibility_score - kwOverview.prev_visibility}%
                    </span>
                  )}
                </div>
                <div>
                  <span className="text-gray-500">Avg Position</span>
                  <span className="ml-2 text-white font-bold">{scan.avg_position ? `#${parseFloat(scan.avg_position).toFixed(1)}` : "N/A"}</span>
                </div>
                <div>
                  <span className="text-gray-500">Best</span>
                  <span className="ml-2 text-green-400 font-bold">{scan.best_position ? `#${scan.best_position}` : "N/A"}</span>
                  {scan.best_location && <span className="text-gray-600 text-xs ml-1">({scan.best_location})</span>}
                </div>
                <div>
                  <span className="text-gray-500">Worst</span>
                  <span className="ml-2 text-red-400 font-bold">{scan.worst_position ? `#${scan.worst_position}` : "N/A"}</span>
                  {scan.worst_location && <span className="text-gray-600 text-xs ml-1">({scan.worst_location})</span>}
                </div>
              </div>
              <span className="text-gray-600 text-xs ml-auto">
                Scanned {new Date(scan.created_at).toLocaleString()}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-dark-300 rounded-lg p-1 w-fit border border-dark-50">
        {[
          { key: "heatmap", label: "Heatmap", icon: MapPin },
          { key: "table", label: "Results Table", icon: Search },
          { key: "trends", label: "Trends", icon: TrendingUp },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === key ? "bg-copper text-white" : "text-gray-400 hover:text-white"
            }`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="bg-dark-300 rounded-xl border border-dark-50 p-12 text-center">
          <RefreshCw size={24} className="animate-spin text-copper mx-auto mb-2" />
          <p className="text-gray-500 text-sm">Loading ranking data...</p>
        </div>
      ) : (
        <>
          {/* Heatmap */}
          {tab === "heatmap" && (
            <div className="bg-dark-300 rounded-xl border border-dark-50 overflow-hidden" style={{ height: 500 }}>
              {points.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <div className="text-center">
                    <MapPin size={40} className="mx-auto mb-3 text-gray-600" />
                    <p className="text-lg font-medium">No scan data yet</p>
                    <p className="text-sm mt-1">Select a keyword and click "Scan Now" to start tracking</p>
                  </div>
                </div>
              ) : (
                <MapContainer
                  center={[41.0, -73.85]}
                  zoom={10}
                  style={{ height: "100%", width: "100%", background: "#1A1A2E" }}
                  zoomControl={true}
                >
                  <TileLayer
                    url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                    attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                  />
                  <FitBounds points={points} />
                  {points.map((pt, i) => (
                    <CircleMarker
                      key={i}
                      center={[parseFloat(pt.latitude), parseFloat(pt.longitude)]}
                      radius={14}
                      fillColor={posColor(pt.position)}
                      color="#1A1A2E"
                      weight={2}
                      opacity={1}
                      fillOpacity={0.85}
                    >
                      <Tooltip
                        direction="top"
                        offset={[0, -10]}
                        permanent={false}
                        className="dark-tooltip"
                      >
                        <div className="text-xs">
                          <strong>{pt.label}</strong>
                          <br />
                          Position: {posLabel(pt.position)}
                          {pt.local_pack_position && (
                            <>
                              <br />
                              Local Pack: #{pt.local_pack_position}
                            </>
                          )}
                        </div>
                      </Tooltip>
                    </CircleMarker>
                  ))}
                </MapContainer>
              )}

              {/* Legend */}
              {points.length > 0 && (
                <div className="absolute bottom-4 left-4 bg-dark-300/90 rounded-lg px-3 py-2 border border-dark-50 flex items-center gap-3 text-xs z-[1000]">
                  {[
                    { color: "#22c55e", label: "#1-3" },
                    { color: "#84cc16", label: "#4-10" },
                    { color: "#eab308", label: "#11-20" },
                    { color: "#ef4444", label: "20+" },
                    { color: "#6b7280", label: "N/R" },
                  ].map(({ color, label }) => (
                    <div key={label} className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: color }} />
                      <span className="text-gray-400">{label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Table */}
          {tab === "table" && (
            <div className="bg-dark-300 rounded-xl border border-dark-50 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-dark-50 text-gray-500 text-left">
                    <th className="px-4 py-3 font-medium">Location</th>
                    <th className="px-4 py-3 font-medium">Organic Rank</th>
                    <th className="px-4 py-3 font-medium">Local Pack</th>
                    <th className="px-4 py-3 font-medium">SERP Features</th>
                    <th className="px-4 py-3 font-medium">Featured Snippet</th>
                  </tr>
                </thead>
                <tbody>
                  {points.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center py-8 text-gray-600">No data available</td>
                    </tr>
                  )}
                  {points.map((pt, i) => (
                    <tr key={i} className="border-b border-dark-50/50 hover:bg-dark-200/50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: posColor(pt.position) }} />
                          <span className="text-white font-medium">{pt.label}</span>
                          {pt.neighborhood && <span className="text-gray-600 text-xs">({pt.neighborhood})</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-bold" style={{ color: posColor(pt.position) }}>
                          {posLabel(pt.position)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {pt.local_pack_position ? (
                          <span className="text-green-400 font-medium">#{pt.local_pack_position}</span>
                        ) : (
                          <span className="text-gray-600">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 flex-wrap">
                          {(pt.serp_features || []).map((f, j) => (
                            <span key={j} className="px-1.5 py-0.5 bg-dark-50 rounded text-[10px] text-gray-400">
                              {f.replace("_", " ")}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {pt.featured_snippet ? (
                          <span className="text-copper font-medium">Yes</span>
                        ) : (
                          <span className="text-gray-600">No</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Trends */}
          {tab === "trends" && (
            <div className="bg-dark-300 rounded-xl border border-dark-50 p-6">
              {trends.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <TrendingUp size={40} className="mx-auto mb-3 text-gray-600" />
                  <p>Not enough scan history to show trends</p>
                  <p className="text-sm mt-1">Run multiple scans over time to see trends</p>
                </div>
              ) : (
                <div className="space-y-6">
                  <h3 className="text-white font-semibold">Visibility Score Over Time</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={trends.map((t) => ({ ...t, date: new Date(t.created_at).toLocaleDateString() }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="date" stroke="#666" fontSize={12} />
                      <YAxis stroke="#666" fontSize={12} />
                      <ReTooltip
                        contentStyle={{ backgroundColor: "#16213E", border: "1px solid #333", borderRadius: 8 }}
                        labelStyle={{ color: "#999" }}
                      />
                      <Line type="monotone" dataKey="visibility_score" stroke="#B87333" strokeWidth={2} dot={{ fill: "#B87333" }} name="Visibility %" />
                    </LineChart>
                  </ResponsiveContainer>

                  <h3 className="text-white font-semibold">Average Position Over Time</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={trends.map((t) => ({ ...t, date: new Date(t.created_at).toLocaleDateString() }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="date" stroke="#666" fontSize={12} />
                      <YAxis reversed stroke="#666" fontSize={12} domain={[1, "auto"]} />
                      <ReTooltip
                        contentStyle={{ backgroundColor: "#16213E", border: "1px solid #333", borderRadius: 8 }}
                        labelStyle={{ color: "#999" }}
                      />
                      <Line type="monotone" dataKey="avg_position" stroke="#22c55e" strokeWidth={2} dot={{ fill: "#22c55e" }} name="Avg Position" />
                      <Line type="monotone" dataKey="best_position" stroke="#84cc16" strokeWidth={1} dot={false} name="Best Position" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Scan History */}
      {scans.length > 0 && (
        <div className="bg-dark-300 rounded-xl border border-dark-50">
          <div className="px-4 py-3 border-b border-dark-50">
            <h3 className="text-white font-semibold text-sm">Scan History</h3>
          </div>
          <div className="divide-y divide-dark-50/50">
            {scans.slice(0, 10).map((s) => (
              <div key={s.id} className="flex items-center justify-between px-4 py-3 text-sm hover:bg-dark-200/50">
                <div className="flex items-center gap-4">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    s.status === "completed" ? "bg-green-500/10 text-green-400" :
                    s.status === "running" ? "bg-blue-500/10 text-blue-400" :
                    s.status === "failed" ? "bg-red-500/10 text-red-400" :
                    "bg-gray-500/10 text-gray-400"
                  }`}>
                    {s.status}
                  </span>
                  <span className="text-white">{s.keyword_text}</span>
                  <span className="text-gray-500">{s.scan_type}</span>
                </div>
                <div className="flex items-center gap-6 text-gray-500">
                  {s.visibility_score > 0 && <span>Visibility: <span className="text-copper font-medium">{s.visibility_score}%</span></span>}
                  {s.avg_position && <span>Avg: <span className="text-white">#{parseFloat(s.avg_position).toFixed(1)}</span></span>}
                  <span>{new Date(s.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Overview table (all keywords) */}
      {overview.length > 0 && (
        <div className="bg-dark-300 rounded-xl border border-dark-50">
          <div className="px-4 py-3 border-b border-dark-50">
            <h3 className="text-white font-semibold text-sm">All Keywords Overview</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-50 text-gray-500 text-left">
                <th className="px-4 py-2 font-medium">Keyword</th>
                <th className="px-4 py-2 font-medium">Visibility</th>
                <th className="px-4 py-2 font-medium">Avg Position</th>
                <th className="px-4 py-2 font-medium">Best Position</th>
                <th className="px-4 py-2 font-medium">Trend</th>
                <th className="px-4 py-2 font-medium">Last Scan</th>
              </tr>
            </thead>
            <tbody>
              {overview.map((o) => {
                const visDelta = o.prev_visibility != null ? o.visibility_score - o.prev_visibility : null;
                return (
                  <tr
                    key={o.keyword_id}
                    className="border-b border-dark-50/50 hover:bg-dark-200/50 cursor-pointer"
                    onClick={() => {
                      const kw = keywords.find((k) => k.id === o.keyword_id);
                      if (kw) setSelectedKw(kw);
                    }}
                  >
                    <td className="px-4 py-3 text-white font-medium">{o.keyword}</td>
                    <td className="px-4 py-3">
                      <span className="text-copper font-bold">{o.visibility_score}%</span>
                    </td>
                    <td className="px-4 py-3 text-white">
                      {o.avg_position ? `#${parseFloat(o.avg_position).toFixed(1)}` : "N/A"}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-green-400 font-medium">
                        {o.best_position ? `#${o.best_position}` : "N/A"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {visDelta !== null ? (
                        <span className={`flex items-center gap-1 ${visDelta >= 0 ? "text-green-400" : "text-red-400"}`}>
                          {visDelta >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                          {visDelta >= 0 ? "+" : ""}{visDelta}%
                        </span>
                      ) : (
                        <span className="text-gray-600">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {new Date(o.scanned_at).toLocaleDateString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {showGridModal && <GridPointModal siteId={siteId} onClose={() => setShowGridModal(false)} />}
      {showSerpModal && <SerpConfigModal onClose={() => setShowSerpModal(false)} />}

      {/* Custom tooltip styles */}
      <style>{`
        .dark-tooltip {
          background: #16213E !important;
          border: 1px solid #333 !important;
          border-radius: 6px !important;
          color: #fff !important;
          padding: 6px 10px !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.4) !important;
        }
        .dark-tooltip::before {
          border-top-color: #16213E !important;
        }
      `}</style>
    </div>
  );
}
